import React, { useState } from 'react';
import { usePermissions } from '../App';
import DataOnboardingTool from './DataOnboardingTool';

// Tailwind utility presets for consistent styling
const btnPrimary =
  'inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary =
  'inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass =
  'block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6';
const selectClass = inputClass;
const checkboxClass = 'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500';
const sectionCard = 'rounded-xl border border-gray-200 bg-white shadow-sm';

// Mirrors ImportViewSet.MAX_BULK_ZIP_SIZE_BYTES (legal_ops/views.py) - keep
// in sync. Checked client-side purely for instant feedback; the backend
// enforces the real limit.
const MAX_ZIP_SIZE_BYTES = 150 * 1024 * 1024; // 150MB

// Accessible table for row-level results
function RowResults({ results }) {
  if (!results || results.length === 0) return null;
  return (
    <div className="mt-4" aria-label="Row level import results">
      <h5 className="text-md font-semibold mb-2">Row Results</h5>
      <div className="max-h-64 overflow-auto border rounded" role="table" aria-rowcount={results.length}>
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-gray-50" role="rowgroup">
            <tr role="row">
              <th className="px-2 py-1 text-left" role="columnheader">Row</th>
              <th className="px-2 py-1 text-left" role="columnheader">Status</th>
              <th className="px-2 py-1 text-left" role="columnheader">ID</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {results.slice(0, 200).map(r => (
              <tr key={r.row} role="row" className="even:bg-gray-50">
                <td className="px-2 py-1" role="cell">{r.row}</td>
                <td className="px-2 py-1" role="cell">
                  <span className={
                    r.status === 'created' ? 'text-green-600' :
                    r.status === 'updated' ? 'text-blue-600' :
                    r.status === 'skipped' ? 'text-yellow-600' : 'text-red-600'
                  }>{r.status}</span>
                </td>
                <td className="px-2 py-1" role="cell">{r.id || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {results.length > 200 && (
          <div className="text-xs text-gray-600 px-2 py-1">Showing first 200 rows…</div>
        )}
      </div>
    </div>
  );
}

function SummaryPanel({ summary, title = 'Import Summary' }) {
  if (!summary) return null;
  const { errors = [], processed = 0, created = 0, updated = 0, dry_run = false } = summary;
  return (
    <div className="mt-4" aria-live="polite">
      <h4 className="text-lg font-semibold mb-2">{title}</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat label="Processed" value={processed} />
        <Stat label="Created" value={created} />
        <Stat label="Updated" value={updated} />
        <Stat label="Mode" value={dry_run ? 'Dry Run' : 'Committed'} />
      </div>
      {errors.length > 0 && (
        <div className="mt-3 text-red-700">
          <p className="font-medium">Errors ({errors.length})</p>
          <ul className="list-disc pl-6 max-h-40 overflow-auto text-xs">
            {errors.slice(0, 25).map((e, idx) => (
              <li key={idx}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
            ))}
            {errors.length > 25 && <li>…and {errors.length - 25} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 rounded border bg-white shadow-sm" role="group" aria-label={label}>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

export default function ImportWizard({ authAxios, showFormMessage, documentTypeOptions }) {
  const [tab, setTab] = useState('csv'); // 'zip' | 'csv' - default to CSV (new tool)

  // ZIP state
  const [zipFile, setZipFile] = useState(null);
  const [caseMatch, setCaseMatch] = useState('case_number'); // 'case_number' | 'title'
  const [useManifest, setUseManifest] = useState(false);
  const [createMissingCases, setCreateMissingCases] = useState(false);
  const [defaultClientEmail, setDefaultClientEmail] = useState('');
  const [defaultDocType, setDefaultDocType] = useState('Other');
  const [zipSummary, setZipSummary] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [zipValidatedOk, setZipValidatedOk] = useState(false);

  // CSV state
  const [csvEntity, setCsvEntity] = useState('clients');
  const [csvFile, setCsvFile] = useState(null);
  const [csvSummary, setCsvSummary] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvProgress, setCsvProgress] = useState(0);
  const [csvValidatedOk, setCsvValidatedOk] = useState(false);
  const [rowResults, setRowResults] = useState([]);
  const [returnErrorsCsv, setReturnErrorsCsv] = useState(false);
  const [allowMissingCase, setAllowMissingCase] = useState(false); // tasks
  const [createClientsIfMissing, setCreateClientsIfMissing] = useState(false); // cases

  // Accessibility live region
  const [lastStatusMsg, setLastStatusMsg] = useState('');

  const resetZipSummary = () => setZipSummary(null);
  const resetCsvSummary = () => setCsvSummary(null);
  const resetRowResults = () => setRowResults([]);

  // Download manifest template
  const downloadManifestTemplate = () => {
    const csv = [
      'filename,case_number,client_first_name,client_last_name,organization_name,document_type',
      'document1.pdf,CASE-12345,John,Smith,,Pleadings',
      'document2.docx,CASE-12345,John,Smith,,Correspondence',
      'evidence.jpg,CASE-67890,,,Acme Corporation,Evidence',
      'contract.pdf,CASE-11111,Jane,Doe,,Agreement',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'manifest_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // CSV template generator
  const downloadCsvTemplate = (type) => {
    let filename = '';
    let csv = '';
    switch (type) {
      case 'clients':
        filename = 'clients_template.csv';
        csv = [
          'external_id,client_type,first_name,last_name,email,phone_number,address,organization_name',
          'CLIENT-1,individual,John,Doe,john.doe@example.com,+26771111111,"123 Main St, Gaborone",',
          'CLIENT-2,organizational,,,acme@company.co.bw,+26772222222,"Plot 42, Broadhurst",Acme Corp',
        ].join('\n');
        break;
      case 'cases':
        filename = 'cases_template.csv';
        csv = [
          'external_id,title,client_first_name,client_last_name,organization_name,case_type,case_number',
          'CASE-1,Smith vs State,John,Doe,,Litigation,',
          'CASE-2,Commercial Lease Review,,,Acme Corporation,Contract,',
        ].join('\n');
        break;
      case 'tasks':
        filename = 'tasks_template.csv';
        csv = [
          'external_id,title,description,case_title,assignee_username,due_date,status,priority',
          'TASK-1,File Notice,Prepare and file notice,Smith vs State,assattorney,2025-11-30,To-Do,Medium',
        ].join('\n');
        break;
      case 'invoices':
        filename = 'invoices_template.csv';
        csv = [
          'external_id,invoice_number,client_first_name,client_last_name,organization_name,issue_date,due_date,status,subtotal,tax_amount,total_amount,currency',
          'INV-EXT-1,INV-2025-0001,John,Doe,,2025-11-01,2025-12-01,Draft,1000,120,1120,BWP',
          'INV-EXT-2,INV-2025-0002,,,Acme Corporation,2025-11-01,2025-12-01,Draft,5000,600,5600,BWP',
        ].join('\n');
        break;
      case 'service_products':
        filename = 'service_products_template.csv';
        csv = [
          'external_id,name,description,price,type,category,is_active',
          'SP-1,Consultation,Initial consult,150.00,Service,Consultation,true',
        ].join('\n');
        break;
      case 'expenses':
        filename = 'expense_entries_template.csv';
        csv = [
          'external_id,case_title,client_first_name,client_last_name,organization_name,description,date,amount,category,billable',
          'EXP-1,Smith vs State,,,Acme Corp,Travel to court,2025-11-15,89.50,Travel,true',
        ].join('\n');
        break;
      case 'payments':
        filename = 'payments_template.csv';
        csv = [
          'external_id,invoice_number,amount,payment_date,payment_method,notes',
          'PAY-1,INV-2025-0001,200.00,2025-12-05,CASH,Initial payment',
        ].join('\n');
        break;
      case 'time_entries':
        filename = 'time_entries_template.csv';
        csv = [
          'external_id,case_title,client_first_name,client_last_name,organization_name,service_product,description,start_time,end_time,is_billable',
          'TE-1,Smith vs State,John,Doe,,Consultation,Research on matter,2025-11-10T09:00:00Z,2025-11-10T11:00:00Z,true',
        ].join('\n');
        break;
      case 'beneficial_owners':
        filename = 'beneficial_owners_template.csv';
        csv = [
          'external_id,organization_name,first_name,last_name,email,phone_number,physical_residential_address,id_or_passport_number,id_type,country_of_issue,id_expiry_date,date_of_birth,nationality,is_director,is_shareholder,percentage_of_shares,ownership_type',
          'UBO-1,Acme Corporation,Jane,Doe,jane.doe@example.com,+26773333333,"42 Industrial Rd","ID123","Passport","BW","2030-01-01","1990-05-01","BW",true,true,25,Direct',
        ].join('\n');
        break;
      default:
        break;
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const runZip = async (mode) => {
    if (!zipFile) {
      showFormMessage('danger', 'Please select a ZIP file.');
      return;
    }
    if (zipFile.size > MAX_ZIP_SIZE_BYTES) {
      const maxMb = Math.round(MAX_ZIP_SIZE_BYTES / (1024 * 1024));
      const gotMb = (zipFile.size / (1024 * 1024)).toFixed(1);
      showFormMessage('danger', `ZIP file is ${gotMb}MB, exceeding the ${maxMb}MB limit. Please split it into smaller batches.`);
      return;
    }
    setZipLoading(true);
    setZipProgress(0);
    setZipValidatedOk(false);
    try {
      const form = new FormData();
      form.append('zip_file', zipFile);
      form.append('case_match', caseMatch);
      form.append('use_manifest', useManifest ? '1' : '0');
      form.append('create_cases_if_missing', createMissingCases ? '1' : '0');
      if (defaultClientEmail) {
        form.append('default_client_email', defaultClientEmail);
      }
      form.append('default_document_type', defaultDocType);
      const dry = mode === 'validate';
      form.append('dry_run', dry ? '1' : '0');

      const resp = await authAxios.post('imports/bulk-documents/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setZipProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      });
      setZipSummary(resp.data);
      setZipValidatedOk(dry && (!resp.data?.errors || resp.data.errors.length === 0));
      showFormMessage('success', dry ? 'ZIP dry-run complete.' : 'ZIP import complete.');
    } catch (err) {
      console.error('ZIP import failed', err);
      const msg = err.response?.data?.error || 'ZIP import failed';
      showFormMessage('danger', msg);
    } finally {
      setZipLoading(false);
    }
  };

  const runCsv = async (mode) => {
    if (!csvFile) {
      showFormMessage('danger', 'Please select a CSV file.');
      return;
    }
    setCsvLoading(true);
    setCsvProgress(0);
    setCsvValidatedOk(false);
    setRowResults([]);
    try {
      const form = new FormData();
      form.append('csv_file', csvFile);
      form.append('entity', csvEntity);
      const dry = mode === 'validate';
      form.append('dry_run', dry ? '1' : '0');
      if (csvEntity === 'cases') {
        form.append('create_clients_if_missing', createClientsIfMissing ? '1' : '0');
      }
      if (csvEntity === 'tasks') {
        form.append('allow_missing_case', allowMissingCase ? '1' : '0');
      }
      if (returnErrorsCsv) {
        form.append('return_errors_csv', '1');
      }

      const resp = await authAxios.post('imports/csv/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setCsvProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        responseType: returnErrorsCsv ? 'blob' : 'json',
      });

      if (returnErrorsCsv && resp.headers && resp.headers['content-type'] && resp.headers['content-type'].includes('text/csv')) {
        const blob = new Blob([resp.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${csvEntity}_import_errors.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showFormMessage('danger', 'Errors encountered. Downloaded error CSV.');
        setLastStatusMsg('Error CSV downloaded');
      } else {
        setCsvSummary(resp.data);
        setRowResults(resp.data?.row_results || []);
        const noErrors = Array.isArray(resp.data?.errors) ? resp.data.errors.length === 0 : true;
        const processed = resp.data?.processed || 0;
        setCsvValidatedOk(dry && noErrors && processed > 0);
        showFormMessage('success', dry ? 'CSV dry-run complete.' : 'CSV import complete.');
        setLastStatusMsg(dry ? 'Dry run complete' : 'Import complete');
      }
    } catch (err) {
      console.error('CSV import failed', err);
      const msg = err.response?.data?.error || 'CSV import failed';
      setLastStatusMsg('CSV import failed');
      showFormMessage('danger', msg);
    } finally {
      setCsvLoading(false);
    }
  };

  const { canRunImports } = usePermissions();

  if (!canRunImports) {
    return (
      <div className="bg-white rounded shadow p-4">
        <h3 className="text-xl font-semibold mb-2">Imports</h3>
        <p className="text-gray-600">You don't have permission to access imports.</p>
      </div>
    );
  }

  const docTypeOpts = documentTypeOptions || [
    { value: 'Client', label: 'Client Management'},
    { value: 'Pleadings', label: 'Pleadings' },
    { value: 'Correspondence', label: 'Correspondence' },
    { value: 'Evidence', label: 'Evidence' },
    { value: 'Research', label: 'Research' },
    { value: 'Agreement', label: 'Agreement' },
    { value: 'Other', label: 'Other' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-6 md:p-8">
      <div className="mb-6">
        <nav className="isolate inline-flex rounded-md shadow-sm" aria-label="Import sections">
          <button
            type="button"
            className={`${tab === 'csv' ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'} relative inline-flex items-center rounded-l-md px-4 py-2 text-sm font-medium focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600`}
            onClick={() => setTab('csv')}
          >
            📊 CSV Import (New!)
          </button>
          <button
            type="button"
            className={`${tab === 'zip' ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'} relative -ml-px inline-flex items-center rounded-r-md px-4 py-2 text-sm font-medium focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600`}
            onClick={() => setTab('zip')}
          >
            📁 Bulk Documents (ZIP)
          </button>
        </nav>
      </div>

      {tab === 'zip' && (
        <form onSubmit={(e)=>e.preventDefault()} className="space-y-6">
          <div className={`${sectionCard} p-4 sm:p-6`}>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-blue-600" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M2 5a2 2 0 012-2h3.5a1 1 0 01.8.4l1.9 2.6H16a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V5z"/></svg>
              </div>
              <div>
                <h4 className="text-base font-semibold text-gray-900">ZIP structure options</h4>
                <p className="mt-1 text-sm text-gray-600">Choose between folder-based or flat structure with manifest CSV.</p>
              </div>
            </div>
            
            {!useManifest ? (
              <>
                <div className="mt-3 mb-2">
                  <span className="text-sm font-medium text-gray-900">📁 Folder Structure (Traditional)</span>
                </div>
                <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 text-xs text-gray-800 overflow-auto">{`zip-root/\n  CASE-12345/\n    affidavit.pdf\n    evidence_photo.jpg\n  Case Title A/\n    contract.docx\n    correspondence.eml`}</pre>
                <ul className="list-disc pl-6 text-sm mt-3 text-gray-700 space-y-1">
                  <li>Organize documents into folders named after case numbers or titles</li>
                  <li>Use Case Match setting to specify how folders map to cases</li>
                  <li>Enable "Create missing cases" to auto-create cases for unmatched folders</li>
                </ul>
              </>
            ) : (
              <>
                <div className="mt-3 mb-2">
                  <span className="text-sm font-medium text-gray-900">📄 Flat Structure with Manifest CSV</span>
                </div>
                <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 text-xs text-gray-800 overflow-auto">{`zip-root/\n  document1.pdf\n  document2.docx\n  evidence.jpg\n  manifest.csv`}</pre>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-900">manifest.csv format:</p>
                    <button 
                      type="button" 
                      onClick={downloadManifestTemplate}
                      className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                    >
                      Download Template
                    </button>
                  </div>
                  <pre className="bg-white p-2 rounded text-xs text-gray-800 overflow-auto">{`filename,case_number,client_first_name,client_last_name,organization_name,document_type\ndocument1.pdf,CASE-12345,John,Smith,,Pleadings\ndocument2.docx,CASE-12345,John,Smith,,Correspondence\nevidence.jpg,CASE-67890,,,Acme Corp,Evidence`}</pre>
                </div>
                <ul className="list-disc pl-6 text-sm mt-3 text-gray-700 space-y-1">
                  <li><strong>Required columns:</strong> filename, case_number (or case_title)</li>
                  <li><strong>Required for new cases:</strong> client_first_name + client_last_name (individuals) OR organization_name (organizations)</li>
                  <li><strong>Optional columns:</strong> document_type (defaults to your selection below)</li>
                  <li>All files at ZIP root level (no folder organization needed)</li>
                  <li>Easier to generate from database exports</li>
                </ul>
              </>
            )}
          </div>

          <div className={`${sectionCard} p-4 sm:p-6`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-900">ZIP File</label>
                <input type="file" accept=".zip" onChange={(e) => { setZipFile(e.target.files[0] || null); resetZipSummary(); }} className={`${inputClass} mt-1`} aria-describedby="zip-help" />
                <p id="zip-help" className="mt-1 text-xs text-gray-500">.zip files only. Large archives may take longer to upload.</p>
              </div>
              
              <div className="md:col-span-3">
                <label className="flex items-center">
                  <input 
                    id="use-manifest" 
                    type="checkbox" 
                    checked={useManifest} 
                    onChange={(e) => setUseManifest(e.target.checked)} 
                    className={checkboxClass} 
                  />
                  <span className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Use manifest.csv (flat structure)</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Enable this if your ZIP has a flat structure with manifest.csv mapping files to cases
                    </span>
                  </span>
                </label>
              </div>

              {!useManifest && (
                <div>
                  <label className="block text-sm font-medium text-gray-900">Case Match</label>
                  <select value={caseMatch} onChange={(e) => setCaseMatch(e.target.value)} className={`${selectClass} mt-1`}>
                    <option value="case_number">Case Number</option>
                    <option value="title">Case Title</option>
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-900">Default Document Type</label>
                <select value={defaultDocType} onChange={(e) => setDefaultDocType(e.target.value)} className={`${selectClass} mt-1`}>
                  {docTypeOpts.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {useManifest ? 'Used when document_type not specified in manifest' : 'Applies to all files'}
                </p>
              </div>
              
              <div className="md:col-span-3">
                <label className="flex items-center mb-2">
                  <input id="create-missing-cases" type="checkbox" checked={createMissingCases} onChange={(e) => setCreateMissingCases(e.target.checked)} className={checkboxClass} />
                  <span className="ml-3 text-sm font-medium text-gray-900">Create missing cases</span>
                </label>
                
                {createMissingCases && (
                  <div className="ml-7 mt-2">
                    <label htmlFor="default-client-email" className="block text-sm font-medium text-gray-900">
                      Default Client Name {!useManifest && <span className="text-red-600">*</span>}
                    </label>
                    <input 
                      id="default-client-email"
                      type="text" 
                      value={defaultClientEmail} 
                      onChange={(e) => setDefaultClientEmail(e.target.value)} 
                      className={`${inputClass} mt-1`}
                      placeholder="John Smith or Acme Corporation"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {useManifest 
                        ? 'Optional: Used if client name not in manifest. For individuals use "FirstName LastName", for organizations use company name. Client will be auto-created if needed.'
                        : 'Required for folder mode: All new cases will be linked to this client. For individuals use "FirstName LastName" (e.g., "John Smith"), for organizations use company name (e.g., "Acme Corp"). Client will be auto-created if needed.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <button type="button" disabled={zipLoading} className={btnSecondary} onClick={()=>runZip('validate')}>
                {zipLoading ? 'Uploading…' : 'Validate (dry-run)'}
              </button>
              <button type="button" disabled={!zipValidatedOk || zipLoading} className={btnPrimary} onClick={()=>runZip('import')}>
                Import ZIP
              </button>
            </div>

            {(zipLoading || zipProgress>0) && (
              <div className="mt-3">
                <div className="h-2 w-full rounded-full bg-gray-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={zipProgress} aria-label="ZIP upload progress">
                  <div className="h-2 rounded-full bg-blue-600 transition-all" style={{width: `${zipProgress}%`}} />
                </div>
                <p className="mt-1 text-xs text-gray-600">{zipProgress}%</p>
              </div>
            )}
            {zipValidatedOk && (
              <p className="mt-2 text-sm text-green-700">Validation passed. You may now import.</p>
            )}
          </div>

          <SummaryPanel title="ZIP Import Summary" summary={zipSummary} />
        </form>
      )}

      {tab === 'csv' && (
        <DataOnboardingTool authAxios={authAxios} showFormMessage={showFormMessage} />
      )}
    </div>
  );
}
